import {
  Button,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Radio,
  Select,
  Spinner,
  Stack,
  useDisclosure,
} from '@chakra-ui/react'
import type { Acquirer, SimpleItem } from 'BrokerTsLib/src/dtos'
import { ContractAcceptationType } from 'BrokerTsLib/src/enums'
import SelectionType from 'BrokerTsLib/src/enums/SelectionType'
import type { FieldError } from 'BrokerTsLib/src/rhf'
import { useFieldArray, useFormContext } from 'BrokerTsLib/src/rhf'

import { InputGroup } from '@/components/InputGroup'
import { FormBody } from '@/containers/forms/FormBody'
import { FormRow } from '@/containers/forms/FormRow'
import { RadioGroup as CustomRadioGroup } from '@/containers/forms/RadioGroup'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { useSelectedOffersAddonContext } from '@/context/SelectedOffersAddonProvider'
import { useSelections } from '@/hooks'

import type { Step4FormValues } from '../Step4'
import { PreviousContractNumberForm } from './PreviousContractNumberForm'

export const KapitolCreateContractForm = () => {
  const TypeIdMainAcquirer = 1
  const TypeIdSecondaryAcquirer = 2

  const availablePercents: SimpleItem[] = [
    { id: 1, value: '0', name: '0%' },
    { id: 2, value: '10', name: '10%' },
    { id: 3, value: '20', name: '20%' },
    { id: 4, value: '30', name: '30%' },
    { id: 5, value: '40', name: '40%' },
    { id: 6, value: '50', name: '50%' },
    { id: 7, value: '60', name: '60%' },
    { id: 8, value: '70', name: '70%' },
    { id: 9, value: '80', name: '80%' },
    { id: 10, value: '90', name: '90%' },
    { id: 11, value: '100', name: '100%' },
  ]

  const { isContracted, isDisabled } = useInsuranceParams()
  const { addonOffers, isLoadingData } = useSelectedOffersAddonContext()
  const {
    register,
    setValue,
    getValues,
    watch,
    trigger,
    control,
    formState: { errors },
  } = useFormContext<Step4FormValues>()

  const { data: dataCat } = useSelections(SelectionType.FormaSjednani)
  const { data: dataFirstPaymentType } = useSelections(
    SelectionType.ZpusobPrvniPlatby,
  )
  const { data: dataDigitalSignatureType } = useSelections(
    SelectionType.ZpusobDigitalnihoPodpisu,
  )

  const meetingRecord = getValues('meetingRecord')
  const { fields, append, update, remove } = useFieldArray({
    control,
    name: 'meetingRecord.brokerNumbers',
    keyName: 'generatedId',
  })
  const actStartType = watch('meetingRecord.startType')

  const secondaryAcquisitor = fields.find(
    (value) => value.typeId === TypeIdSecondaryAcquirer,
  )
  const { isOpen, onClose, onOpen } = useDisclosure({
    isOpen: !!secondaryAcquisitor,
  })

  let enabledAcceptTypesCount = 0
  if (meetingRecord?.paymentEnabled) enabledAcceptTypesCount++
  if (meetingRecord?.signatureEnabled) enabledAcceptTypesCount++
  if (meetingRecord?.digitalSignatureEnabled) enabledAcceptTypesCount++

  const addSecondary = () => {
    const acq: Acquirer = {
      acquisitionNumber: '',
      id: 0,
      percentage: 0,
      typeId: TypeIdSecondaryAcquirer,
    }

    append(acq)
    onOpen()
  }

  const removeSecondary = () => {
    remove(fields.findIndex((x) => x.typeId == TypeIdSecondaryAcquirer))

    // automaticky upravit podíl hlavního získatele
    const mainAcqIndex = fields.findIndex(
      (acq) => acq.typeId === TypeIdMainAcquirer,
    )
    if (mainAcqIndex >= 0) {
      const mainAcq = fields.find((acq) => acq.typeId === TypeIdMainAcquirer)
      update(mainAcqIndex, {
        ...mainAcq!,
        percentage: 100,
      })
    }

    onClose()
  }

  const updatePercent = (value: string, index: number, field: Acquirer) => {
    const podilnikPerc = parseInt(value)
    update(index, {
      ...field,
      percentage: podilnikPerc,
    })

    // automaticky upravit podíl hlavního získatele
    const mainAcqIndex = fields.findIndex(
      (acq) => acq.typeId === TypeIdMainAcquirer,
    )
    if (mainAcqIndex != -1) {
      const mainAcq = fields.find((acq) => acq.typeId === TypeIdMainAcquirer)
      update(mainAcqIndex, {
        ...mainAcq!,
        percentage: 100 - podilnikPerc,
      })
    }
  }

  const updateAcqNumber = (value: string, index: number, field: Acquirer) => {
    update(index, {
      ...field,
      acquisitionNumber: value,
    })
  }

  return (
    <FormBody>
      <FormRow>
        <FormControl isInvalid={!!errors.meetingRecord?.startType?.message}>
          <FormLabel>
            Způsob sjednání
            {isLoadingData && <Spinner ml={2} />}
          </FormLabel>

          {dataCat?.data && (
            <CustomRadioGroup
              {...register('meetingRecord.startType')}
              valueTransform={(value) => value?.toString()}
              onChange={(e) => {
                setValue('meetingRecord.startType', parseInt(e))
                trigger('meetingRecord.startType')
              }}
              isDisabled={enabledAcceptTypesCount < 2 || isDisabled}
              stackProps={{
                direction: 'column',
                spacing: 4,
              }}
            >
              {dataCat?.data
                .sort(
                  (it1, it2) =>
                    (it1.displayOrder ?? 0) - (it2.displayOrder ?? 0),
                )
                .map((item) => (
                  <Radio
                    value={item.value}
                    key={item.id}
                    isDisabled={
                      (addonOffers.length > 0 &&
                        Number(item.value) ==
                        ContractAcceptationType.Signature) ||
                      isDisabled ||
                      isLoadingData ||
                      (!meetingRecord?.paymentEnabled &&
                        Number(item.value) ==
                        ContractAcceptationType.Payment) ||
                      (!meetingRecord?.signatureEnabled &&
                        Number(item.value) ==
                        ContractAcceptationType.Signature) ||
                      (!meetingRecord?.digitalSignatureEnabled &&
                        Number(item.value) ==
                        ContractAcceptationType.DigitalSignature)
                    }
                  >
                    {item.description}
                  </Radio>
                ))}
            </CustomRadioGroup>
          )}
        </FormControl>

        {(meetingRecord?.firstPaymentTypePaymentGateEnabled ||
          meetingRecord?.firstPaymentTypeBankTransferEnabled) &&
          actStartType == ContractAcceptationType.Payment && (
          <FormControl
            isInvalid={!!errors.meetingRecord?.firstPaymentType?.message}
          >
            <FormLabel>Způsob první platby</FormLabel>

            {dataFirstPaymentType?.data && (
              <CustomRadioGroup
                {...register('meetingRecord.firstPaymentType')}
                valueTransform={(value) => value?.toString()}
                onChange={(e) => {
                  setValue('meetingRecord.firstPaymentType', parseInt(e))
                  trigger('meetingRecord.firstPaymentType')
                }}
                stackProps={{
                  direction: 'column',
                  spacing: 4,
                }}
              >
                {dataFirstPaymentType?.data
                  .sort(
                    (it1, it2) =>
                      (it1.displayOrder ?? 0) - (it2.displayOrder ?? 0),
                  )
                  .map((item) => (
                    <Radio value={item.id.toString()} key={item.id} isDisabled={(item.name === 'PaymentGate' && !meetingRecord.firstPaymentTypePaymentGateEnabled) || (item.name === 'Bank' && !meetingRecord.firstPaymentTypeBankTransferEnabled) || isDisabled}>
                      {item.description}
                    </Radio>
                  ))}
              </CustomRadioGroup>
            )}
          </FormControl>
        )}

        {dataDigitalSignatureType?.data &&
          dataDigitalSignatureType.data.length > 0 &&
          actStartType == ContractAcceptationType.DigitalSignature && (
          <FormControl
            isInvalid={!!errors.meetingRecord?.digitalSignatureType?.message}
          >
            <FormLabel>Způsob digitálního podpisu</FormLabel>

            {dataDigitalSignatureType?.data && (
              <CustomRadioGroup
                {...register('meetingRecord.digitalSignatureType')}
                valueTransform={(value) => value?.toString()}
                onChange={(e) => {
                  setValue('meetingRecord.digitalSignatureType', parseInt(e))
                  trigger('meetingRecord.digitalSignatureType')
                }}
                isDisabled={isDisabled}
                stackProps={{
                  direction: 'column',
                  spacing: 4,
                }}
              >
                {dataDigitalSignatureType?.data
                  .filter(
                    (it) =>
                      (meetingRecord.allowedDigitalSignatureTypes?.indexOf(
                        it.id,
                      ) ?? -1) != -1,
                  )
                  .sort(
                    (it1, it2) =>
                      (it1.displayOrder ?? 0) - (it2.displayOrder ?? 0),
                  )
                  .map((item) => (
                    <Radio value={item.id.toString()} key={item.id}>
                      {item.description}
                    </Radio>
                  ))}
              </CustomRadioGroup>
            )}
          </FormControl>
        )}
      </FormRow>

      <PreviousContractNumberForm></PreviousContractNumberForm>

      {!isContracted && (
        <>
          {!isOpen && (
            <Flex justifyContent="left" w="100%">
              <Button type="button" variant="solid" onClick={addSecondary}>
                Přidat podílníka
              </Button>
            </Flex>
          )}

          {isOpen && (
            <Stack>
              {fields.map((item, index) => {
                let acquisitionNumberError: FieldError | undefined = undefined
                if (errors.meetingRecord?.brokerNumbers) {
                  acquisitionNumberError =
                    errors.meetingRecord.brokerNumbers[index]?.acquisitionNumber
                }

                return (
                  item.typeId === TypeIdSecondaryAcquirer && (
                    <FormRow itemsCount={2} key={index}>
                      <FormControl isInvalid={!!acquisitionNumberError}>
                        <FormLabel>Další podílník</FormLabel>

                        <InputGroup
                          {...register(
                            `meetingRecord.brokerNumbers.${index}.acquisitionNumber` as const,
                          )}
                          value={getValues(
                            `meetingRecord.brokerNumbers.${index}.acquisitionNumber`,
                          )}
                          errors={acquisitionNumberError}
                          onChange={(value) =>
                            updateAcqNumber(value.target.value, index, item)
                          }
                          htmlSize={16}
                        />

                        <FormHelperText>
                          Zadejte osobní číslo dalšího podílníka
                        </FormHelperText>
                      </FormControl>

                      <FormControl>
                        <FormLabel htmlFor="podilnikProcent">Podíl</FormLabel>

                        <Select
                          id="podilnikProcent"
                          value={getValues(
                            `meetingRecord.brokerNumbers.${index}.percentage`,
                          ).toString()}
                          onChange={(value) =>
                            updatePercent(value.target.value, index, item)
                          }
                        >
                          {availablePercents.map((item) => (
                            <option value={item.value} key={item.id}>
                              {item.name}
                            </option>
                          ))}
                        </Select>
                      </FormControl>
                    </FormRow>
                  )
                )
              })}

              <Flex justifyContent="center" w="100%" mb={8}>
                <Button
                  type="button"
                  variant="solid"
                  colorScheme="red"
                  onClick={removeSecondary}
                >
                  Odebrat podílníka
                </Button>
              </Flex>
            </Stack>
          )}
        </>
      )}

      {isContracted && (
        <FormRow>
          <FormControl
            isInvalid={!!errors.meetingRecord?.brokerNumbers?.message}
          >
            <FormLabel>Získatelské číslo</FormLabel>

            {fields.map((field, index) => (
              <InputGroup
                key={field.acquisitionNumber}
                {...register(
                  `meetingRecord.brokerNumbers.${index}.acquisitionNumber`,
                )}
                placeholder="Např. 12348765"
                isDisabled
                rightElement={`${getValues(`meetingRecord.brokerNumbers.${index}.percentage`)}%`}
              />
            ))}
          </FormControl>
        </FormRow>
      )}
    </FormBody>
  )
}

export default KapitolCreateContractForm
